import React from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { CreditCard, Building2, Wallet, DollarSign, Check, ShieldCheck } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const BRAND_METHODS = [
    { v: "card", label: "Credit / debit card", Icon: CreditCard, desc: "We'll charge ad spend + creator payouts here." },
    { v: "stripe", label: "Stripe account", Icon: ShieldCheck, desc: "Connect your Stripe to bill." },
    { v: "bank", label: "Bank transfer", Icon: Building2, desc: "Wire / ACH from your bank." },
    { v: "paypal", label: "PayPal", Icon: Wallet, desc: "Pay through PayPal Business." },
];

const CREATOR_METHODS = [
    { v: "stripe", label: "Stripe Connect", Icon: ShieldCheck, desc: "Get paid into your Stripe account." },
    { v: "paypal", label: "PayPal", Icon: Wallet, desc: "Receive instant transfers." },
    { v: "bank", label: "Bank account", Icon: Building2, desc: "Direct deposit / IBAN." },
];

export default function PayoutForm() {
    const { user } = useAuth();
    const [profile, setProfile] = React.useState(null);
    const [form, setForm] = React.useState({
        method: "",
        label: "",
        card_last4: "",
        card_brand: "",
        stripe_account_id: "",
        bank_holder: "",
        bank_iban: "",
        bank_routing: "",
        bank_account: "",
        paypal_email: "",
        billing_email: "",
        country: "",
    });
    const [saving, setSaving] = React.useState(false);

    const methods = user?.role === "creator" ? CREATOR_METHODS : BRAND_METHODS;
    const isBrand = user?.role === "brand";

    const load = React.useCallback(() => {
        api.get("/payouts/me")
            .then((r) => {
                setProfile(r.data);
                if (r.data.method) {
                    setForm((f) => ({ ...f, ...r.data }));
                } else {
                    setForm((f) => ({ ...f, method: methods[0].v }));
                }
            })
            .catch(() => {});
    }, [methods]);

    React.useEffect(() => {
        load();
    }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.method) return toast.error("Pick a payment method");
        setSaving(true);
        try {
            await api.put("/payouts/me", form);
            toast.success("Payout details saved (mocked)");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;

    return (
        <div className="space-y-6" data-testid="payouts-section">
            <div className="card-ecom p-5 surface-warm flex items-center gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-2xl bg-coral/10 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-coral" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-semibold text-slate-900">
                        {isBrand ? "Pay creators from" : "Get paid to"}
                    </div>
                    <div className="text-sm text-slate-500">
                        {isBrand
                            ? "Where Castline charges your ad spend and creator payouts."
                            : "Where you'd like Castline to send your earnings."}{" "}
                        <span className="font-semibold">All MOCKED for the MVP.</span>
                    </div>
                </div>
                {profile?.configured && (
                    <span className="chip chip-success">
                        <Check className="w-3 h-3" /> Configured
                    </span>
                )}
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="payout-form">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {methods.map(({ v, label, Icon, desc }) => (
                        <button
                            type="button"
                            key={v}
                            onClick={() => setForm({ ...form, method: v })}
                            className={`p-4 rounded-2xl border text-left transition-all ${
                                form.method === v
                                    ? "border-coral bg-coral/5 shadow-sm"
                                    : "border-slate-200 hover:border-slate-300"
                            }`}
                            data-testid={`payout-method-${v}`}
                        >
                            <Icon
                                className={`w-5 h-5 mb-3 ${form.method === v ? "text-coral" : "text-slate-500"}`}
                            />
                            <div className="font-display text-base font-semibold text-slate-900">
                                {label}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
                        </button>
                    ))}
                </div>

                {/* Method-specific fields */}
                {form.method === "card" && (
                    <div className="card-ecom p-5 grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Card brand</Label>
                            <Input
                                value={form.card_brand}
                                onChange={(e) => setForm({ ...form, card_brand: e.target.value })}
                                placeholder="Visa, Mastercard…"
                                className="rounded-xl border-slate-200 h-11"
                                data-testid="payout-card-brand"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Last 4 digits</Label>
                            <Input
                                value={form.card_last4}
                                onChange={(e) =>
                                    setForm({ ...form, card_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })
                                }
                                placeholder="4242"
                                className="rounded-xl border-slate-200 h-11"
                                data-testid="payout-card-last4"
                            />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <Label className="text-sm font-medium">Billing email</Label>
                            <Input
                                type="email"
                                value={form.billing_email}
                                onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
                                className="rounded-xl border-slate-200 h-11"
                                data-testid="payout-billing-email"
                            />
                        </div>
                    </div>
                )}

                {form.method === "stripe" && (
                    <div className="card-ecom p-5 space-y-4">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Stripe account ID</Label>
                            <Input
                                value={form.stripe_account_id}
                                onChange={(e) =>
                                    setForm({ ...form, stripe_account_id: e.target.value })
                                }
                                placeholder="acct_1Nq…"
                                className="rounded-xl border-slate-200 h-11 font-mono"
                                data-testid="payout-stripe-id"
                            />
                            <p className="text-xs text-slate-500">
                                Real Stripe Connect OAuth wires in later — paste your account id for
                                now.
                            </p>
                        </div>
                    </div>
                )}

                {form.method === "bank" && (
                    <div className="card-ecom p-5 grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2 sm:col-span-2">
                            <Label className="text-sm font-medium">Account holder</Label>
                            <Input
                                value={form.bank_holder}
                                onChange={(e) => setForm({ ...form, bank_holder: e.target.value })}
                                className="rounded-xl border-slate-200 h-11"
                                data-testid="payout-bank-holder"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">IBAN (EU)</Label>
                            <Input
                                value={form.bank_iban}
                                onChange={(e) => setForm({ ...form, bank_iban: e.target.value })}
                                placeholder="GB29 NWBK 6016 1331…"
                                className="rounded-xl border-slate-200 h-11 font-mono"
                                data-testid="payout-bank-iban"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Routing # (US)</Label>
                            <Input
                                value={form.bank_routing}
                                onChange={(e) => setForm({ ...form, bank_routing: e.target.value })}
                                placeholder="121000248"
                                className="rounded-xl border-slate-200 h-11 font-mono"
                                data-testid="payout-bank-routing"
                            />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <Label className="text-sm font-medium">Account #</Label>
                            <Input
                                value={form.bank_account}
                                onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                                className="rounded-xl border-slate-200 h-11 font-mono"
                                data-testid="payout-bank-account"
                            />
                        </div>
                    </div>
                )}

                {form.method === "paypal" && (
                    <div className="card-ecom p-5">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">PayPal email</Label>
                            <Input
                                type="email"
                                value={form.paypal_email}
                                onChange={(e) => setForm({ ...form, paypal_email: e.target.value })}
                                className="rounded-xl border-slate-200 h-11"
                                data-testid="payout-paypal-email"
                            />
                        </div>
                    </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Label (optional)</Label>
                        <Input
                            value={form.label}
                            onChange={(e) => setForm({ ...form, label: e.target.value })}
                            placeholder="Primary account"
                            className="rounded-xl border-slate-200 h-11"
                            data-testid="payout-label"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Country</Label>
                        <Input
                            value={form.country}
                            onChange={(e) => setForm({ ...form, country: e.target.value })}
                            placeholder="US, ES, UK…"
                            className="rounded-xl border-slate-200 h-11"
                            data-testid="payout-country"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={saving}
                    className="btn-pill-primary"
                    data-testid="payout-submit"
                >
                    {saving ? "Saving…" : profile?.configured ? "Update payout details" : "Save payout details"}
                </button>
            </form>
        </div>
    );
}
